import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import ClientHostWorkEvent from './ClientHostWorkEvent';
import ClientStarRanking from './ClientStarRanking';

const ClientHostEvents = ({ kakoId }) => {
  const [isMember, setIsMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteLink, setInviteLink] = useState('');
  const [visitedTabs, setVisitedTabs] = useState({});
  const [trabalhouGanhouActive, setTrabalhouGanhouActive] = useState(false);
  const [rankingEstrelasActive, setRankingEstrelasActive] = useState(false);

  useEffect(() => {
    if (kakoId) {
      checkMembership();
      loadEventConfigs();
    }
  }, [kakoId]);

  const loadEventConfigs = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Carregar config do Trabalhou Ganhou
      try {
        const trabalhouRes = await axios.get(`${BACKEND_URL}/api/host-work-event/config`);
        setTrabalhouGanhouActive(trabalhouRes.data.active || false);
      } catch (error) {
        console.error('Erro ao carregar config Trabalhou Ganhou:', error);
        setTrabalhouGanhouActive(false);
      }
      
      // Carregar config do Ranking de Estrelas
      try {
        const rankingRes = await axios.get(`${BACKEND_URL}/api/star-ranking/config`);
        setRankingEstrelasActive(rankingRes.data.active || false);
      } catch (error) {
        console.error('Erro ao carregar config Ranking Estrelas:', error);
        setRankingEstrelasActive(false);
      }
    } catch (error) {
      console.error('Erro ao carregar configs:', error);
    }
  };

  const markTabAsVisited = (tabName) => {
    setVisitedTabs(prev => ({ ...prev, [tabName]: true }));
  };

  const checkMembership = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/host-work-event/check-membership/${kakoId}`);
      setIsMember(response.data.is_member);
      setInviteLink(response.data.invite_link);
    } catch (error) {
      console.error('Erro ao verificar membership:', error);
      setIsMember(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  // Se NÃO é membro da agência
  if (!isMember) {
    return (
      <Card className="border-2 border-orange-400">
        <CardContent className="p-8 text-center space-y-4">
          <div className="text-6xl">🦊</div>
          <h3 className="text-xl font-bold text-gray-900">Somente Hosts da FOX AGENCY</h3>
          <p className="text-gray-600">
            Os eventos desta aba são exclusivos para hosts oficialmente vinculados à FOX AGENCY.
          </p>
          <p className="text-sm text-gray-500">
            Faça parte da nossa agência e aproveite recompensas exclusivos!
          </p>
          <Button
            onClick={() => window.open(inviteLink, '_blank')}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-3 text-lg"
          >
            🦊 Solicitar Entrada na Agência
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Se É membro da agência, mostra eventos
  // Verifica quais eventos estão ativos
  const hasAnyEventActive = trabalhouGanhouActive || rankingEstrelasActive;
  
  if (!hasAnyEventActive) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          <div className="text-6xl mb-4">🦊</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Eventos Temporariamente Inativos</h3>
          <p className="text-gray-600">
            Não há eventos ativos no momento. Volte em breve!
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Define qual tab deve ser a padrão (primeiro evento ativo)
  const defaultTab = trabalhouGanhouActive ? 'trabalhou-ganhou' : 'ranking-estrelas';
  
  // Se apenas um evento está ativo, não mostra tabs, mostra direto o evento
  if (trabalhouGanhouActive && !rankingEstrelasActive) {
    return (
      <div className="space-y-4">
        <ClientHostWorkEvent kakoId={kakoId} />
      </div>
    );
  }
  
  if (!trabalhouGanhouActive && rankingEstrelasActive) {
    return (
      <div className="space-y-4">
        <ClientStarRanking kakoId={kakoId} />
      </div>
    );
  }
  
  // Se AMBOS estão ativos, mostra as tabs
  return (
    <div className="space-y-4">
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {trabalhouGanhouActive && (
            <TabsTrigger 
              value="trabalhou-ganhou"
              className={`bg-purple-200 data-[state=active]:bg-purple-600 data-[state=active]:text-white ${
                !visitedTabs.trabalhouganhou ? 'neon-purple' : ''
              }`}
              onClick={() => markTabAsVisited('trabalhouganhou')}
              onMouseDown={() => markTabAsVisited('trabalhouganhou')}
            >
              💼 Trabalhou Ganhou
            </TabsTrigger>
          )}
          {rankingEstrelasActive && (
            <TabsTrigger 
              value="ranking-estrelas"
              className={`bg-yellow-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-500 data-[state=active]:to-orange-500 data-[state=active]:text-white ${
                !visitedTabs.rankingestrelas ? 'neon-yellow' : ''
              }`}
              onClick={() => markTabAsVisited('rankingestrelas')}
              onMouseDown={() => markTabAsVisited('rankingestrelas')}
            >
              ⭐ Ranking de Estrelas
            </TabsTrigger>
          )}
        </TabsList>

        {trabalhouGanhouActive && (
          <TabsContent value="trabalhou-ganhou">
            <ClientHostWorkEvent kakoId={kakoId} />
          </TabsContent>
        )}

        {rankingEstrelasActive && (
          <TabsContent value="ranking-estrelas">
            <ClientStarRanking kakoId={kakoId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ClientHostEvents;
